/** Weergavelogica voor de DSO-subkoppen, los van React zodat hij te testen is. */

type Resultaat = Record<string, unknown>;

/** Velden die iets over de analyse zeggen, niet over de regel zelf. */
const META = new Set(["vanToepassing", "bronWId", "eenheid"]);

const leeg = (v: unknown) =>
  v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

const waarde = (v: unknown): string => {
  if (Array.isArray(v)) return v.map(waarde).filter(Boolean).join(", ");
  if (v && typeof v === "object")
    return Object.entries(v)
      .filter(([, w]) => !leeg(w))
      .map(([k, w]) => `${k} ${waarde(w)}`)
      .join(", ");
  return leeg(v) ? "" : String(v);
};

const generiek = (resultaat: Resultaat) =>
  Object.entries(resultaat)
    .filter(([k, v]) => !META.has(k) && !leeg(v))
    .map(([k, v]) => `${k}: ${waarde(v)}`)
    .join("; ") || null;

const BEKEND: Record<string, (r: Resultaat) => string | null> = {
  "max-bouwhoogte": ({ maxBouwhoogte, eenheid, voorwaarde }) => {
    const hoogte = leeg(maxBouwhoogte) ? "" : `${maxBouwhoogte} ${eenheid || "m"}`;
    if (!hoogte) return leeg(voorwaarde) ? null : String(voorwaarde);
    return leeg(voorwaarde) ? hoogte : `${hoogte} (${voorwaarde})`;
  },
  geluidnormen: ({ normen }) =>
    Array.isArray(normen) && normen.length
      ? normen.map((n: { waardeDb: number }) => `${n.waardeDb} dB`).join(", ")
      : null,
};

/**
 * Een geëxtraheerde regel als leesbare tekst. Bekende soorten netjes ("12 m",
 * "50 dB"); een soort die de monitor later toevoegt verschijnt vanzelf als
 * "sleutel: waarde" — ruw, maar nooit stuk. Niets te melden → `null`.
 */
export const analyseTekst = (analyse: string, resultaat: Resultaat): string | null =>
  // Mist een bekende soort zijn eigen velden, dan zijn die leeg en toont de
  // generieke vorm wat er wél is
  BEKEND[analyse]?.(resultaat) || generiek(resultaat);

/**
 * "art. 22.66 lid 1" uit de wId-staart van het DSO (`["art_22.66", "para_1"]`)
 * of uit een kaal IMRO-artikelnummer (`"10"`).
 */
export const artikelLabel = (artikel: string | string[]): string => {
  if (!Array.isArray(artikel)) return artikel ? `art. ${artikel}` : "";
  return artikel
    .map((deel) =>
      deel
        .replace(/^art_/, "art. ")
        .replace(/^para_/, "lid ")
        .replace(/_/g, " ")
    )
    .join(" ");
};

/** "gemeente · 12 artikelen van toepassing" onder de titel van een regeling. */
export const regelingRegel = (r: {
  bestuurslaag: string;
  soort: string | null;
  artikelen: number;
}) =>
  [
    r.bestuurslaag || r.soort,
    r.artikelen
      ? `${r.artikelen} ${r.artikelen === 1 ? "artikel" : "artikelen"} van toepassing`
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

const LAGEN = ["gemeente", "waterschap", "provincie", "rijk"];
// Wat bindt eerst; visie en programma's sturen alleen het bestuur zelf
const SOORTEN = [/plan|verordening|amvb|regeling/i, /visie/i, /programma/i];

type Sorteerbaar = { bestuurslaag: string; soort: string | null };

const achteraanAlsOnbekend = (i: number, lengte: number) => (i < 0 ? lengte : i);
const laagRang = (r: Sorteerbaar) =>
  achteraanAlsOnbekend(LAGEN.indexOf(r.bestuurslaag), LAGEN.length);
const soortRang = (r: Sorteerbaar) =>
  achteraanAlsOnbekend(
    SOORTEN.findIndex((s) => s.test(r.soort ?? "")),
    SOORTEN.length
  );

/**
 * Het dichtstbije bestuur eerst, en daarbinnen wat bindt vóór visie en
 * programma's: anders staat het omgevingsplan achter vier provinciale
 * programma's. Alleen voor de weergave — de chatprompt houdt de volgorde van
 * de monitor.
 */
export const sorteerRegelingen = <T extends Sorteerbaar>(regelingen: T[]) =>
  [...regelingen].sort((a, b) => laagRang(a) - laagRang(b) || soortRang(a) - soortRang(b));

/**
 * De kaartlink naast de titel, alleen als hij iets toevoegt. Bij een regeling
 * leest `url` de tekst en toont `kaartUrl` Regels op de kaart; bij een IMRO-plan
 * vallen ze samen (er is geen resolver), en dan is één link genoeg.
 * Beide komen kant-en-klaar van de monitor — wij bouwen er geen na.
 */
export const kaartLink = (item: { url?: string | null; kaartUrl?: string | null }) =>
  item.kaartUrl && item.kaartUrl !== item.url ? item.kaartUrl : undefined;
